import { useForm } from '@tanstack/react-form';
import { Button } from '@/components/ui/button';
import { CONFIRM_OTP_FORM } from '@/constants/auth.constants';
import { FloatingLabelInput } from '@/components/ui/input';
import { useMutation } from '@tanstack/react-query';
import { confirmOTP } from '@/services/apis/auth/confirmOTP';
import parse from 'html-react-parser';
import { sendOTP } from '@/services/apis/auth/sendOTP';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { useSendOTPMutationState } from '@/services/hooks/useSendOTPMutationState';
import { useGlobalStore } from '@/stores/store';
import { FieldError } from '@/components/ui/form-field-error';
import { replaceNonDigit } from '@/utils/string';
import { hasFieldErrors } from '@/utils/forms';
import { useAuthDetails } from '@/hooks/use-auth-details';
import { z } from 'zod';
import { getGlobalStore } from '@/utils/store';

const createConfirmOTPSchema = (digitCount: number) => {
  return z.object({
    otpCode: z
      .string({ message: 'Code is required' })
      .min(digitCount, { message: `Code must be at least ${digitCount}` }),
  });
};

const ConfirmOTPForm = () => {
  const { setAuthFlowCurrentStep, setLoginAccessToken, setIsAuthSheetLoading } =
    getGlobalStore();
  const customerId = useGlobalStore((state) => state.authStore.customerId);
  const loginAccessToken = useGlobalStore(
    (state) => state.authStore.loginAccessToken
  );
  const isAuthSheetLoading = useGlobalStore(
    (state) => state.authStore.isAuthSheetLoading
  );
  const sendOTPMutationResponse = useSendOTPMutationState();
  const digitCount = sendOTPMutationResponse.data.digitCount;

  useAuthDetails({ isEnabled: loginAccessToken !== '' });

  const form = useForm({
    defaultValues: {
      otpCode: '',
    },
    validators: {
      onChange: createConfirmOTPSchema(digitCount),
    },
    onSubmit: ({ value }) => {
      setIsAuthSheetLoading(true);
      confirmOTPmutate({
        channel: sendOTPMutationResponse.variables.channel,
        otpCode: value.otpCode,
      });
    },
  });

  const { mutate: confirmOTPmutate, isError: isConfirmError } = useMutation({
    mutationFn: confirmOTP,
    onSuccess: async (data) => {
      setLoginAccessToken(data.access_token);
    },
    onError: (error) => {
      console.error(error);
      setIsAuthSheetLoading(false);
      // Propagate error from API to the form (No matter what the error is)
      form.setFieldMeta('otpCode', (prevMeta) => ({
        ...prevMeta,
        errorMap: {
          onChange: {
            message: CONFIRM_OTP_FORM.FALLBACK_CONFIRM_ERROR_MESSAGE,
          },
        },
      }));
    },
  });

  const { mutate: resendOTPmutate, isPending: isResendPending } = useMutation({
    mutationFn: sendOTP,
  });
  const handleOnGoBack = () => {
    if (isAuthSheetLoading) return;
    setAuthFlowCurrentStep('verifyAccount');
  };

  if (!customerId) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 text-grey-900">
      <p className="text-xl font-nunito700">{CONFIRM_OTP_FORM.TITLE}</p>
      <p className="text-base">{parse(sendOTPMutationResponse.data.label)}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="space-y-6"
      >
        <form.Field name="otpCode">
          {(field) => (
            <div className="flex flex-col gap-1">
              <FloatingLabelInput
                name="otpCode"
                maxLength={digitCount}
                value={field.state.value}
                onChange={(e) =>
                  field.handleChange(replaceNonDigit(e.target.value))
                }
                label="Code"
                size="large"
                hasError={isConfirmError || hasFieldErrors(field)}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>
        <div className="flex flex-col gap-6 lg:space-y-0 lg:flex-row lg:gap-x-4">
          <form.Subscribe
            selector={(state) => [state.canSubmit]}
            children={([canSubmit]) => (
              <Button
                variant="primary"
                type="submit"
                size="medium"
                disabled={!canSubmit}
                loading={isAuthSheetLoading}
                className="flex-none"
              >
                {CONFIRM_OTP_FORM.SUBMIT_BUTTON}
              </Button>
            )}
          />
          <Button
            type="button"
            variant="secondary"
            loading={isResendPending}
            size="medium"
            onClick={() =>
              resendOTPmutate({
                channel: sendOTPMutationResponse.variables.channel,
                customerId,
              })
            }
          >
            {CONFIRM_OTP_FORM.RESEND_CODE_BUTTON}
          </Button>
        </div>
        <button
          disabled={isAuthSheetLoading}
          className="flex items-center w-fit lg:mt-10! hover:bg-green-50 hover:rounded-md cursor-pointer group disabled:hover:cursor-default disabled:hover:bg-white"
          onClick={handleOnGoBack}
        >
          <p className="text-sm lg:text-lg font-nunito700 text-warp-green-500 group-disabled:text-grey-200">
            {CONFIRM_OTP_FORM.BACK_BUTTON}
          </p>
          <ChevronRightIcon className="size-5 lg:size-6 text-warp-green-500 group-disabled:text-grey-200" />
        </button>
      </form>
    </div>
  );
};
export { ConfirmOTPForm };
